import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { toast } from "sonner";
import { PageHeader } from "@/components/PageHeader";
import { TablePagination } from "@/components/TablePagination";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Loader2, Printer, IndianRupee, AlertTriangle } from "lucide-react";
import { exportPDF, exportExcel } from "@/lib/exportUtils";
import { SmsButton } from "@/components/SmsButton";

const money = (n) => `₹${Number(n || 0).toFixed(2)}`;
const monthStart = () => { const d = new Date(); d.setDate(1); return d.toISOString().slice(0, 10); };
const today = () => new Date().toISOString().slice(0, 10);
const EMPTY = { farmer_id: "", from_date: monthStart(), to_date: today(), advance: 0, deduction: 0, feed_deduction: 0, bonus: 0, remarks: "" };

export default function Payments() {
  const { tenant, user } = useAuth();
  const { t, lang } = useI18n();
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [preview, setPreview] = useState(null);
  const [receipt, setReceipt] = useState(null);
  const [applyFeed, setApplyFeed] = useState(false);

  const { data: farmers } = useQuery({ queryKey: ["farmers-all"], queryFn: async () => (await api.get("/farmers/all")).data });
  const { data, isLoading } = useQuery({ queryKey: ["payments", page], queryFn: async () => (await api.get("/payments", { params: { page, page_size: 10 } })).data });

  const doPreview = async (next) => {
    const f = next || form;
    if (!f.farmer_id) return;
    try { setPreview((await api.get("/payments/preview", { params: { farmer_id: f.farmer_id, from_date: f.from_date, to_date: f.to_date } })).data); }
    catch (e) { setPreview(null); }
  };

  const create = useMutation({
    mutationFn: async () => (await api.post("/payments", {
      ...form, advance: Number(form.advance || 0), deduction: Number(form.deduction || 0), bonus: Number(form.bonus || 0),
      feed_deduction: applyFeed ? Number(form.feed_deduction || 0) : 0,
    })).data,
    onSuccess: (res) => { toast.success(`Payment ${res.receipt_number} generated`); setReceipt(res); setOpen(false); setForm(EMPTY); setPreview(null); setApplyFeed(false); qc.invalidateQueries({ queryKey: ["payments"] }); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const set = (k) => (e) => { const v = e?.target ? e.target.value : e; const next = { ...form, [k]: v }; setForm(next); if (["farmer_id", "from_date", "to_date"].includes(k)) doPreview(next); };
  const net = preview ? (preview.gross_amount - Number(form.advance || 0) - Number(form.deduction || 0) - (applyFeed ? Number(form.feed_deduction || 0) : 0) + Number(form.bonus || 0)) : 0;

  const exportRows = (data?.items || []).map((p) => ({
    ...p, from_to: `${p.from_date} → ${p.to_date}`,
    total_deductions: (p.advance || 0) + (p.deduction || 0) + (p.feed_deduction || 0),
  }));
  const cols = [
    { key: "receipt_number", header: "Receipt" }, { key: "farmer_name", header: "Farmer" },
    { key: "from_to", header: "Period" }, { key: "gross_amount", header: "Gross" },
    { key: "feed_deduction", header: "Feed Ded." }, { key: "total_deductions", header: "Deductions" },
    { key: "net_amount", header: "Net" },
  ];

  const canSms = user?.role === "tenant_owner";
  const mobileOf = (fid) => (farmers || []).find((f) => f.id === fid)?.mobile || "";
  const paymentSms = (p) => `नमस्ते ${p.farmer_name}, ${tenant?.center_name || ""} कडून तुमचे ${p.from_date} ते ${p.to_date} या कालावधीचे दूध पेमेंट ₹${Number(p.net_amount || 0).toFixed(2)} जमा झाले आहे. पावती: ${p.receipt_number}`;

  return (
    <div className="space-y-6" data-testid="payments-page">
      <PageHeader
        title={t("pay.title")}
        subtitle={t("pay.subtitle")}
        breadcrumb={[t("crumb.center"), t("pay.title")]}
        actions={
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => exportExcel({ columns: cols, rows: exportRows, filename: "payments" })} data-testid="export-payments-excel"><IndianRupee className="mr-1.5 h-4 w-4" /> {t("common.excel")}</Button>
            <Button variant="outline" onClick={() => exportPDF({ title: "Payments", columns: cols, rows: exportRows, filename: "payments", subtitle: tenant?.center_name })} data-testid="export-payments-pdf">{t("common.pdf")}</Button>
            <Button onClick={() => { setForm(EMPTY); setApplyFeed(false); setOpen(true); doPreview(EMPTY); }} data-testid="generate-payment-btn"><Plus className="mr-1.5 h-4 w-4" /> {t("pay.generate")}</Button>
          </div>
        }
      />

      <Card>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow><TableHead>{t("pay.receipt")}</TableHead><TableHead>{t("farmers.farmer")}</TableHead><TableHead>{t("pay.period")}</TableHead><TableHead className="text-right">{t("pay.gross")}</TableHead><TableHead className="text-right">{t("pay.deductions")}</TableHead><TableHead className="text-right">{t("pay.net")}</TableHead><TableHead className="text-right"></TableHead></TableRow></TableHeader>
            <TableBody>
              {isLoading
                ? Array.from({ length: 6 }).map((_, i) => <TableRow key={i}><TableCell colSpan={7}><Skeleton className="h-8" /></TableCell></TableRow>)
                : (data?.items || []).map((p) => (
                    <TableRow key={p.id} className="hover:bg-muted/50" data-testid={`payment-row-${p.receipt_number}`}>
                      <TableCell className="tnum text-xs">{p.receipt_number}</TableCell>
                      <TableCell><p className="font-medium">{p.farmer_name}</p><p className="text-xs text-muted-foreground">{p.collection_count} collections</p></TableCell>
                      <TableCell className="text-xs">{p.from_date} → {p.to_date}</TableCell>
                      <TableCell className="text-right tnum">{money(p.gross_amount)}</TableCell>
                      <TableCell className="text-right tnum text-destructive">-{money((p.advance || 0) + (p.deduction || 0) + (p.feed_deduction || 0))}</TableCell>
                      <TableCell className="text-right font-semibold tnum">{money(p.net_amount)}</TableCell>
                      <TableCell className="text-right">
                        {canSms && <SmsButton mobile={mobileOf(p.farmer_id)} message={paymentSms(p)} testId={`sms-payment-${p.receipt_number}`} />}
                        <Button variant="ghost" size="icon" onClick={() => setReceipt(p)}><Printer className="h-4 w-4" /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
              {!isLoading && data?.items?.length === 0 && <TableRow><TableCell colSpan={7} className="py-10 text-center text-muted-foreground">{t("pay.none")}</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
        <TablePagination page={page} pages={data?.pages} total={data?.total} onPage={setPage} />
      </Card>

      {/* Generate dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">{t("pay.generate")}</DialogTitle></DialogHeader>
          <div className="grid gap-4 py-2 sm:grid-cols-2">
            <div className="space-y-1.5 sm:col-span-2">
              <Label>{t("farmers.farmer")}</Label>
              <Select value={form.farmer_id} onValueChange={set("farmer_id")}>
                <SelectTrigger data-testid="payment-farmer-select"><SelectValue placeholder={t("col.selectFarmer")} /></SelectTrigger>
                <SelectContent>{(farmers || []).map((f) => <SelectItem key={f.id} value={f.id}>{f.farmer_code} · {f.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>{t("common.from")}</Label><Input type="date" value={form.from_date} onChange={set("from_date")} data-testid="payment-from-input" /></div>
            <div className="space-y-1.5"><Label>{t("common.to")}</Label><Input type="date" value={form.to_date} onChange={set("to_date")} data-testid="payment-to-input" /></div>
            <div className="space-y-1.5"><Label>{t("pay.advance")}</Label><Input type="number" value={form.advance} onChange={set("advance")} data-testid="payment-advance-input" /></div>
            <div className="space-y-1.5"><Label>{t("pay.deduction")}</Label><Input type="number" value={form.deduction} onChange={set("deduction")} data-testid="payment-deduction-input" /></div>
            <div className="space-y-1.5"><Label>{t("pay.bonus")}</Label><Input type="number" value={form.bonus} onChange={set("bonus")} data-testid="payment-bonus-input" /></div>
          </div>
          {preview && preview.feed_pending > 0 && (
            <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-3" data-testid="feed-pending-alert">
              <div className="flex items-start gap-2">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-destructive">{t("feed.pendingAlert")} <span className="tnum">{money(preview.feed_pending)}</span>.</p>
                  <label className="mt-2 flex items-center gap-2 text-sm">
                    <Checkbox checked={applyFeed} onCheckedChange={(v) => { const on = !!v; setApplyFeed(on); setForm((f) => ({ ...f, feed_deduction: on ? preview.feed_pending : 0 })); }} data-testid="apply-feed-deduction-checkbox" />
                    {t("feed.applyDeduction")}
                  </label>
                  {applyFeed && (
                    <div className="mt-2 space-y-1.5">
                      <Label className="text-xs">{t("feed.deductionAmount")}</Label>
                      <Input type="number" value={form.feed_deduction} onChange={set("feed_deduction")} max={preview.feed_pending} data-testid="feed-deduction-input" />
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          <div className="rounded-lg border bg-muted/40 p-3 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">{t("pay.grossUnpaid")}</span><span className="font-semibold tnum" data-testid="payment-gross">{preview ? money(preview.gross_amount) : "—"}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">{t("pay.collections")}</span><span className="tnum">{preview?.collection_count ?? "—"}</span></div>
            <div className="mt-1 flex justify-between border-t pt-1"><span className="text-muted-foreground">{t("pay.netPayable")}</span><span className="font-heading text-lg font-bold text-primary tnum" data-testid="payment-net">{money(net)}</span></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>{t("common.cancel")}</Button>
            <Button onClick={() => create.mutate()} disabled={!preview || !preview.collection_count || create.isPending} data-testid="submit-payment-btn">{create.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} {t("pay.generate")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment receipt */}
      <Dialog open={!!receipt} onOpenChange={(o) => !o && setReceipt(null)}>
        <DialogContent className="sm:max-w-xs">
          <DialogHeader><DialogTitle className="font-heading">Payment Receipt</DialogTitle></DialogHeader>
          {receipt && (
            <div id="thermal-receipt" className="mx-auto w-[280px] rounded border bg-white p-4 font-mono text-[12px] text-black">
              <div className="text-center"><p className="text-[14px] font-bold">{tenant?.center_name}</p><p className="my-1 border-y border-dashed border-black py-0.5">PAYMENT RECEIPT</p></div>
              <div className="flex justify-between"><span>Receipt</span><span>{receipt.receipt_number}</span></div>
              <div className="flex justify-between"><span>Farmer</span><span>{receipt.farmer_name}</span></div>
              <div className="flex justify-between"><span>Period</span><span>{receipt.from_date}→{receipt.to_date}</span></div>
              <div className="my-1 border-t border-dashed border-black" />
              <div className="flex justify-between"><span>Gross</span><span>{money(receipt.gross_amount)}</span></div>
              <div className="flex justify-between"><span>Advance</span><span>-{money(receipt.advance)}</span></div>
              <div className="flex justify-between"><span>Deduction</span><span>-{money(receipt.deduction)}</span></div>
              {receipt.feed_deduction > 0 && <div className="flex justify-between"><span>Feed Ded.</span><span>-{money(receipt.feed_deduction)}</span></div>}
              <div className="flex justify-between"><span>Bonus</span><span>+{money(receipt.bonus)}</span></div>
              <div className="my-1 border-t border-dashed border-black" />
              <div className="flex justify-between text-[14px] font-bold"><span>NET PAID</span><span>{money(receipt.net_amount)}</span></div>
            </div>
          )}
          <div className="flex gap-2">
            {canSms && receipt && <SmsButton mobile={mobileOf(receipt.farmer_id)} message={paymentSms(receipt)} variant="outline" size="default" testId="sms-payment-receipt" />}
            <Button className="flex-1" onClick={() => window.print()} data-testid="print-payment-btn"><Printer className="mr-1.5 h-4 w-4" /> Print</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
